import { Box, Typography } from '@mui/material';
import { ConversionSpeed as SpeedType } from 'hooks/analytics';
import { SectionCard, SectionTitle, AnalyticsColors } from '../../components/analyticsUi';
import InfoTooltip from 'components/v2/common/infoTooltip';
import { CONVERSION_SPEED } from '../../constants/metricDescriptions';

interface ConversionSpeedProps {
    current: SpeedType;
    comparison: SpeedType;
}

const stages: { key: keyof SpeedType; label: string; color: string }[] = [
    { key: 'avgDaysToAssessment', label: 'Registration → Assessment', color: '#2563EB' },
    { key: 'avgDaysToOffer', label: 'Assessment → Offer Sent', color: '#0891B2' },
    { key: 'avgDaysToPaid', label: 'Offer Sent → Paid', color: '#059669' },
];

const ConversionSpeedComponent = ({ current, comparison }: ConversionSpeedProps) => {
    return (
        <SectionCard>
            <SectionTitle>
                Conversion Speed
                <InfoTooltip title={CONVERSION_SPEED} />
            </SectionTitle>

            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(3, 1fr)',
                    gap: '1.5rem',
                    '@media (max-width: 768px)': { gridTemplateColumns: '1fr' },
                }}
            >
                {stages.map((s) => {
                    const currentVal = current[s.key];
                    const compVal = comparison[s.key];
                    const diff = compVal - currentVal;
                    const faster = diff > 0;

                    return (
                        <Box
                            key={s.key}
                            sx={{
                                p: '1.25rem',
                                background: AnalyticsColors.surfaceBg,
                                border: `1px solid ${AnalyticsColors.borderLight}`,
                                borderRadius: '10px',
                                textAlign: 'center',
                                borderTop: `3px solid ${s.color}`,
                                transition: 'box-shadow 0.15s ease',
                                '&:hover': { boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)' },
                            }}
                        >
                            <Typography
                                sx={{
                                    fontSize: '11.5px',
                                    fontWeight: 600,
                                    color: AnalyticsColors.textSecondary,
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.5px',
                                    mb: '0.75rem',
                                }}
                            >
                                {s.label}
                            </Typography>
                            <Typography
                                sx={{
                                    fontSize: 32,
                                    fontWeight: 700,
                                    color: AnalyticsColors.textPrimary,
                                    lineHeight: 1.1,
                                }}
                            >
                                {currentVal.toFixed(1)}
                            </Typography>
                            <Typography
                                sx={{
                                    fontSize: 12,
                                    color: AnalyticsColors.textTertiary,
                                    mt: '0.2rem',
                                    mb: '0.75rem',
                                }}
                            >
                                avg days
                            </Typography>
                            <Box
                                component="span"
                                sx={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '3px',
                                    fontSize: 12,
                                    fontWeight: 600,
                                    p: '3px 10px',
                                    borderRadius: '6px',
                                    background: faster ? '#DCFCE7' : '#FEE2E2',
                                    color: faster ? '#166534' : '#991B1B',
                                }}
                            >
                                {faster ? '↓' : '↑'} {Math.abs(diff).toFixed(1)}d vs prev
                            </Box>
                        </Box>
                    );
                })}
            </Box>
        </SectionCard>
    );
};

export default ConversionSpeedComponent;
